import React, { useState } from "react";
import Button from "../../../components/ui/Button";
import Input from "../../../components/ui/Input";
import Select from "../../../components/ui/Select";
import { Checkbox } from "../../../components/ui/Checkbox";
import Icon from "../../../components/AppIcon";

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    projectType: "",
    budget: "",
    timeline: "",
    message: "",
    updates: false,
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const projectTypes = [
    { value: "wedding", label: "Wedding & Events" },
    { value: "realestate", label: "Real Estate Marketing" },
    { value: "marketing", label: "Brand & Marketing Content" },
    { value: "design", label: "Design Fiction & Conceptual" },
    { value: "corporate", label: "Corporate Videos" },
    { value: "other", label: "Other (Please specify)" },
  ];

  const budgetRanges = [
    { value: "under-5k", label: "Under $5,000" },
    { value: "5k-10k", label: "$5,000 - $10,000" },
    { value: "10k-25k", label: "$10,000 - $25,000" },
    { value: "25k-50k", label: "$25,000 - $50,000" },
    { value: "over-50k", label: "Over $50,000" },
    { value: "discuss", label: "Let's discuss" },
  ];

  const timelines = [
    { value: "urgent", label: "ASAP (Rush job)" },
    { value: "1-2weeks", label: "1-2 weeks" },
    { value: "1month", label: "Within 1 month" },
    { value: "2-3months", label: "2-3 months" },
    { value: "3-6months", label: "3-6 months" },
    { value: "flexible", label: "Flexible timeline" },
  ];

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e?.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    // Clear error when user starts typing
    if (errors?.[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.name?.trim()) {
      newErrors.name = "Name is required";
    }

    if (!formData?.email?.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/?.test(formData?.email)) {
      newErrors.email = "Please enter a valid email";
    }

    if (!formData?.phone?.trim()) {
      newErrors.phone = "Phone number is required";
    }

    if (!formData?.projectType) {
      newErrors.projectType = "Please select a project type";
    }

    if (!formData?.message?.trim()) {
      newErrors.message = "Please describe your project";
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 2000));
      console.log("Form submitted:", formData);

      // Reset form
      setFormData({
        name: "",
        email: "",
        phone: "",
        company: "",
        projectType: "",
        budget: "",
        timeline: "",
        message: "",
        updates: false,
      });

      alert("Thank you! We'll get back to you within 2 hours.");
    } catch (error) {
      console.error("Submission error:", error);
      alert("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-16 bg-muted/30">
      <div className="container mx-auto px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-fluid-3xl font-display font-bold text-primary mb-4">
              Start Your Project Today
            </h2>
            <p className="text-fluid-lg text-muted-foreground max-w-2xl mx-auto">
              Tell us about your vision and we'll create a custom proposal
              tailored to your needs and budget.
            </p>
          </div>

          <div className="bg-card border border-border rounded-lg shadow-lg overflow-hidden">
            <div className="grid grid-cols-1 lg:grid-cols-3">
              {/* Form Section */}
              <div className="lg:col-span-2 p-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Personal Information */}
                  <div>
                    <h3 className="text-lg font-semibold text-card-foreground mb-4">
                      Contact Information
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <Input
                        label="Full Name"
                        type="text"
                        name="name"
                        placeholder="Enter your full name"
                        value={formData?.name}
                        onChange={handleInputChange}
                        error={errors?.name}
                        required
                      />

                      <Input
                        label="Company (Optional)"
                        type="text"
                        name="company"
                        placeholder="Your company name"
                        value={formData?.company}
                        onChange={handleInputChange}
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                      <Input
                        label="Email Address"
                        type="email"
                        name="email"
                        placeholder="your@email.com"
                        value={formData?.email}
                        onChange={handleInputChange}
                        error={errors?.email}
                        required
                      />

                      <Input
                        label="Phone Number"
                        type="tel"
                        name="phone"
                        placeholder="(555) 123-4567"
                        value={formData?.phone}
                        onChange={handleInputChange}
                        error={errors?.phone}
                        required
                      />
                    </div>
                  </div>

                  {/* Project Details */}
                  <div>
                    <h3 className="text-lg font-semibold text-card-foreground mb-4">
                      Project Details
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <Select
                        label="Project Type"
                        placeholder="Select project type"
                        options={projectTypes}
                        value={formData?.projectType}
                        onChange={(value) =>
                          setFormData((prev) => ({
                            ...prev,
                            projectType: value,
                          }))
                        }
                        error={errors?.projectType}
                        required
                      />

                      <Select
                        label="Budget Range"
                        placeholder="Select budget range"
                        options={budgetRanges}
                        value={formData?.budget}
                        onChange={(value) =>
                          setFormData((prev) => ({ ...prev, budget: value }))
                        }
                      />
                    </div>

                    <div className="mt-4">
                      <Select
                        label="Timeline"
                        placeholder="When do you need this completed?"
                        options={timelines}
                        value={formData?.timeline}
                        onChange={(value) =>
                          setFormData((prev) => ({ ...prev, timeline: value }))
                        }
                      />
                    </div>
                  </div>

                  {/* Project Description */}
                  <div>
                    <label className="block text-sm font-medium text-card-foreground mb-2">
                      Project Description *
                    </label>
                    <textarea
                      name="message"
                      rows={5}
                      className={`w-full px-3 py-2 border rounded-md text-sm placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent ${
                        errors?.message ? "border-destructive" : "border-border"
                      }`}
                      placeholder="Tell us about your project vision, specific requirements, locations, key dates, and any other important details..."
                      value={formData?.message}
                      onChange={handleInputChange}
                    />
                    {errors?.message && (
                      <p className="mt-1 text-sm text-destructive">
                        {errors?.message}
                      </p>
                    )}
                  </div>

                  {/* Preferences */}
                  <div className="space-y-3">
                    <Checkbox
                      label="Send me updates about new services and portfolio additions"
                      checked={formData?.updates}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          updates: e?.target?.checked,
                        }))
                      }
                    />
                  </div>

                  <Button
                    type="submit"
                    variant="default"
                    size="lg"
                    fullWidth
                    loading={isSubmitting}
                    className="bg-cta hover:bg-cta/90 text-cta-foreground"
                    iconName="Send"
                    iconPosition="left"
                  >
                    {isSubmitting ? "Sending..." : "Send Project Inquiry"}
                  </Button>

                  <p className="text-xs text-muted-foreground text-center">
                    By submitting this form, you agree to our privacy policy.
                    We'll respond within 2 hours during business hours.
                  </p>
                </form>
              </div>

              {/* Info Sidebar */}
              <div className="bg-muted/50 p-8 border-l border-border">
                <h3 className="text-lg font-semibold text-card-foreground mb-6">
                  What Happens Next?
                </h3>

                <div className="space-y-6">
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-accent rounded-full flex items-center justify-center text-accent-foreground font-semibold text-sm">
                      1
                    </div>
                    <div>
                      <h4 className="font-medium text-card-foreground mb-1">
                        Quick Response
                      </h4>
                      <p className="text-sm text-muted-foreground">
                        We'll review your project and respond within 2 hours
                        with initial thoughts and next steps.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-accent rounded-full flex items-center justify-center text-accent-foreground font-semibold text-sm">
                      2
                    </div>
                    <div>
                      <h4 className="font-medium text-card-foreground mb-1">
                        Discovery Call
                      </h4>
                      <p className="text-sm text-muted-foreground">
                        Schedule a free consultation to discuss your vision,
                        timeline, and requirements in detail.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-accent rounded-full flex items-center justify-center text-accent-foreground font-semibold text-sm">
                      3
                    </div>
                    <div>
                      <h4 className="font-medium text-card-foreground mb-1">
                        Custom Proposal
                      </h4>
                      <p className="text-sm text-muted-foreground">
                        Receive a detailed proposal with timeline, deliverables,
                        and pricing tailored to your project.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-8 p-4 bg-accent/10 rounded-lg">
                  <div className="flex items-center space-x-2 mb-2">
                    <Icon name="Shield" size={16} className="text-accent" />
                    <span className="font-medium text-card-foreground">
                      Our Promise
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    No spam, no pressure. We respect your time and will only
                    contact you about your specific project inquiry.
                  </p>
                </div>

                <div className="mt-6 space-y-3">
                  <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                    <Icon name="Clock" size={14} />
                    <span>Response within 2 hours</span>
                  </div>
                  <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                    <Icon name="Award" size={14} />
                    <span>Award-winning team</span>
                  </div>
                  <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                    <Icon name="Users" size={14} />
                    <span>500+ satisfied clients</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;
